import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, ArrowLeft, ExternalLink } from 'lucide-react';
import gsap from 'gsap';
import { submitChatEscalation } from '../lib/aios';
import { routeChat } from '../lib/chatRouter';
import { CONSULTATION_15MIN_URL, CONSULTING_BOOKING_URL } from '../lib/links';
import { walkthroughUrl } from './auraos/walkthrough';
import { BOOKED_COPY, BOOKED_MESSAGE, bookingRecentlyShown, isBookedKind, kindFromUrl, markBookingShown, type BookedKind } from '../lib/bookingConfirmation';
import { trackEvent } from '../lib/analytics';
import type { ChatAction, ChatMessage } from '../lib/types';
import { ContactFormModal } from './ContactFormModal';
import { HoneypotField, isHoneypotTripped } from '../lib/honeypot';
// Provides the .bc-fab / .bc-key-face keycap-press rules used by the
// floating button below (import is deduped by the bundler).
import '../styles/homeTheme.css';

/**
 * ChatChip — clickable pill rendered inside the chat panel for quick-
 * action suggestions returned by the router. The hover animation is
 * byte-identical to PillNav's NavItem: a white `.hover-circle` scales
 * up from the bottom of the pill while the `.pill-label` slides up
 * and a black-colored `.pill-label-hover` slides into view from below.
 * GSAP math is the exact same recipe (circle radius computed from
 * pill geometry, scale-to-3 endpoint, power2.inOut easing, 0.3s).
 *
 * The only difference vs the nav pill is the size envelope, applied
 * via the `.pill-sm` CSS modifier so the chips fit a 380px chat
 * panel without wrapping to 4+ rows.
 */
type ChatChipKind = 'link' | 'external' | 'button';

interface ChatChipProps {
  label: string;
  kind: ChatChipKind;
  /** Internal route, only used when kind === 'link'. */
  to?: string;
  /** External href, only used when kind === 'external'. */
  externalHref?: string;
  /** Click handler, only used when kind === 'button' or 'link'. */
  onClick?: () => void;
}

const ChatChip = ({ label, kind, to, externalHref, onClick }: ChatChipProps) => {
  // Refs typed as the broad HTMLElement so a single set works for
  // <a>, <Link> (renders <a>), and <button>. The GSAP geometry reads
  // offsetWidth / offsetHeight which exist on every HTMLElement.
  const pillRef = useRef<HTMLElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const labelHoverRef = useRef<HTMLSpanElement>(null);
  const tl = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    if (!pillRef.current || !circleRef.current || !labelRef.current || !labelHoverRef.current)
      return;

    // Defer the offsetWidth/offsetHeight read until after the next
    // paint — reading geometry synchronously inside a mount effect
    // causes a forced reflow. Same mitigation as PillNav.NavItem.
    let raf = 0;
    const setup = () => {
      const pill = pillRef.current;
      const circle = circleRef.current;
      const lbl = labelRef.current;
      const lblHover = labelHoverRef.current;
      if (!pill || !circle || !lbl || !lblHover) return;

      const w = pill.offsetWidth;
      const h = pill.offsetHeight;
      // Circle anchored at the bottom of the pill, sized so a 3× scale
      // covers the full pill. Math is identical to PillNav.NavItem.
      const R = (w * w) / 4 / h / 2 + h / 2; // == (w² / 4 + h²) / (2h)
      const D = 2 * R + 2;
      const delta = R - Math.sqrt(R * R - (w * w) / 4) + 1;

      gsap.set(circle, {
        width: D,
        height: D,
        bottom: -delta,
        left: '50%',
        xPercent: -50,
        transformOrigin: `50% ${D - delta}px`,
        scale: 0,
      });

      tl.current = gsap
        .timeline({ paused: true })
        .to(circle, { scale: 3, duration: 0.3, ease: 'power2.inOut' })
        .to(lbl, { y: '-100%', duration: 0.3, ease: 'power2.inOut' }, 0)
        .to(lblHover, { y: '0%', duration: 0.3, ease: 'power2.inOut' }, 0);
    };
    raf = requestAnimationFrame(setup);

    return () => {
      cancelAnimationFrame(raf);
      tl.current?.kill();
    };
  }, []);

  // The inner DOM is shared across all three render variants so the
  // animation refs hook into the same elements regardless of whether
  // we render a <Link>, <a>, or <button>.
  const inner = (
    <>
      <div ref={circleRef} className="hover-circle" />
      <span className="label-stack">
        <span ref={labelRef} className="pill-label">
          {label}
        </span>
        <span
          ref={labelHoverRef}
          className="pill-label-hover translate-y-full"
        >
          {label}
        </span>
      </span>
    </>
  );

  const className = 'pill pill-sm cursor-pointer';
  const onMouseEnter = () => tl.current?.play();
  const onMouseLeave = () => tl.current?.reverse();

  if (kind === 'link' && to) {
    return (
      <Link
        to={to}
        ref={pillRef as React.RefObject<HTMLAnchorElement>}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        {inner}
      </Link>
    );
  }

  if (kind === 'external' && externalHref) {
    return (
      <a
        ref={pillRef as React.RefObject<HTMLAnchorElement>}
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      ref={pillRef as React.RefObject<HTMLButtonElement>}
      type="button"
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {inner}
    </button>
  );
};

/**
 * Sam Assistant — floating chat widget mounted on the home page (and
 * any other page that opts in by including <ChatWidget />). Talks to
 * the AIOS chat endpoint via `routeChat`; falls back to a
 * deterministic rule-based stub router when that call fails so the widget never sits broken on the live site.
 *
 * UX intent: feels like talking to Sam personally — small dark glass
 * card bottom-right, profile photo + name + status dot in the header,
 * message bubbles styled to match the rest of the site (serif
 * accents, italic for Sam's voice, dark backdrop). Closed state is a
 * 56px avatar bubble that pulses on first paint so visitors notice
 * it without it auto-opening.
 */

// Hosted on the BRANDCENTRAL ImageKit CDN. Swap this string + redeploy
// to update the avatar everywhere it appears in the widget. Falls back
// to the BC logo if the URL ever fails to load.
// Self-hosted at /public/founder.jpg — see AboutPage.tsx SAM_PHOTO
// comment. Previously fetched from ik.imagekit.io which set 3rd-party
// Hotjar cookies on every visitor's browser (Lighthouse Best Practices
// flag). Self-hosting moves the cookie surface to brandctl.com only.
const SAM_AVATAR_PATH = '/founder.jpg';
const SAM_AVATAR_FALLBACK = '/bc-logo.png';

/**
 * BcMark — the BRANDCENTRAL keycap monogram used as the assistant's
 * avatar. The assistant is agency infrastructure, not a person, so it
 * wears the brand mark rather than the founder's face; Samuel shows up
 * personally at the hand-off (the escalation form) where it matters.
 */
const BcMark = ({ size = 44, tone = 'dark' }: { size?: number; tone?: 'dark' | 'light' }) => {
  // `tone` picks the palette: 'dark' is the on-light mark (dark lines on a
  // white keycap) used inside the panel header; 'light' is the on-dark
  // variant (light lines on a near-black keycap) — byte-identical to the
  // footer / RouteLoader mark — so the mark stays legible on the black
  // closed-bubble button.
  const line = tone === 'light' ? '#f4f4f5' : '#141414';
  const faceFill = tone === 'light' ? '#17181b' : '#fff';
  const backFill = tone === 'light' ? '#2c2c31' : '#e9e7f0';
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="none" role="img" aria-label="BRANDCENTRAL assistant">
      <rect x="8" y="9" width="48" height="48" rx="14" fill={backFill} stroke={line} strokeWidth="3" />
      {/* Front face — the `bc-key-face` class lets it depress like a real
          keycap when the button around it is pressed (see .bc-fab:active
          in homeTheme.css). Only animates inside the floating button; the
          panel-header mark has no .bc-fab ancestor, so it stays static. */}
      <g className="bc-key-face">
        <rect x="8" y="9" width="48" height="42" rx="14" fill={faceFill} stroke={line} strokeWidth="3" />
        <rect x="14" y="15" width="36" height="30" rx="8" fill="none" stroke={line} strokeWidth="1.4" opacity={tone === 'light' ? 0.3 : 0.28} />
        <text x="32" y="30.5" fontFamily='"Hanken Grotesk", system-ui, sans-serif' fontSize="19" fontWeight="700" fill={line} textAnchor="middle" dominantBaseline="central" letterSpacing="-1.2">BC</text>
      </g>
    </svg>
  );
};

// ── Rate-limit + safety constants ───────────────────────────────────────
// Per-session caps so a malicious/curious visitor can't spam the chat
// surface and drive AIOS model costs (or the localStorage stub) into
// the ground. All numbers picked to feel invisible to a normal human
// but bite well before any abuse pattern shows up in analytics.
const MAX_INPUT_LENGTH = 2000;
const INPUT_WARNING_AT = 1800; // start showing the counter at 1800 chars
const MIN_SEND_INTERVAL_MS = 800; // hard cooldown between user-initiated sends
const MAX_MESSAGES_PER_SESSION = 60; // 30 turns = 60 messages (user+assistant)
const ESCALATION_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes between escalations
const ESCALATION_RATELIMIT_KEY = 'bc.chatEscalationAt'; // localStorage timestamp

interface ContactFormService {
  lockedService?: 'App Development' | 'Intelligent Agents' | 'Web Design' | 'Operations Consulting';
}

const VALID_LOCKED_SERVICES = new Set([
  'App Development',
  'Intelligent Agents',
  'Web Design',
  'Operations Consulting',
]);

// Stricter-than-`.includes('@')` email check. Not RFC-perfect (no email
// regex is) but rejects the obvious garbage strings without locking out
// legitimate `+plus` / subdomain emails.
const EMAIL_RX = /^[^\s@]+@[^\s@.]+\.[^\s@]{2,}$/;
const isValidEmail = (s: string): boolean => EMAIL_RX.test(s.trim());

const ESCALATION_STORAGE_KEY = 'bc.chatEscalationSeen';

/** Read last escalation timestamp from localStorage. Returns 0 if none / unparseable. */
const lastEscalationAt = (): number => {
  try {
    const v = window.localStorage.getItem(ESCALATION_RATELIMIT_KEY);
    const n = v ? Number(v) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const markEscalationSubmitted = () => {
  try {
    window.localStorage.setItem(ESCALATION_RATELIMIT_KEY, String(Date.now()));
  } catch {
    /* swallow quota / private mode */
  }
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [actions, setActions] = useState<ChatAction[]>([]);
  const [escalating, setEscalating] = useState(false);
  const [escalateEmail, setEscalateEmail] = useState('');
  const [escalateMessage, setEscalateMessage] = useState('');
  const [escalateSubmitted, setEscalateSubmitted] = useState(false);
  const [escalateError, setEscalateError] = useState('');
  const [escalateBusy, setEscalateBusy] = useState(false);
  // Honeypot field for the escalation form — bots auto-fill, real
  // visitors don't see it.
  const [escalateHoneypot, setEscalateHoneypot] = useState('');
  const [contactModal, setContactModal] = useState<ContactFormService | null>(null);
  // Inline Cal.com booking. The chat stays the visitor's home base: they book
  // without leaving it, and "Back to chat" returns to the conversation.
  const [booking, setBooking] = useState<{ url: string; title: string } | null>(null);
  const bookingRef = useRef(booking);
  bookingRef.current = booking;

  // After a booking made inside the chat, close the booking view and confirm in
  // the conversation. Two paths: Cal.com's embed event (bookingSuccessful), or
  // Cal.com redirecting the iframe to /?booked=... (the home page then tells us).
  // The flag keeps the message to once, even if the page banner also fires.
  useEffect(() => {
    const confirm = (kind: BookedKind | null) => {
      if (!kind || bookingRecentlyShown()) return;
      markBookingShown(kind);
      setBooking(null);
      setMessages((m) => [...m, { role: 'assistant', content: BOOKED_COPY[kind], ts: new Date().toISOString() }]);
      trackEvent('booking_confirmed', { kind, via: 'chat' });
    };
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: unknown; kind?: unknown; originator?: unknown; action?: unknown } | null;
      if (!d || typeof d !== 'object') return;
      if (e.origin === window.location.origin && d.type === BOOKED_MESSAGE && isBookedKind(d.kind)) {
        confirm(d.kind);
        return;
      }
      let host = '';
      try { host = new URL(e.origin).hostname; } catch { return; }
      if ((host === 'cal.com' || host.endsWith('.cal.com')) && d.originator === 'CAL') {
        const t = String(d.type ?? d.action ?? '');
        if (t.includes('bookingSuccessful') && bookingRef.current) confirm(kindFromUrl(bookingRef.current.url));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const navigate = useNavigate();
  const [avatarErrored, setAvatarErrored] = useState(false);
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track the last send timestamp in a ref (not state) so the cooldown
  // check stays synchronous and doesn't trigger re-renders.
  const lastSendAtRef = useRef(0);

  // Per-tab session ID for server-side session tracking on AIOS.
  // Generated once per tab and persisted in sessionStorage so it
  // survives a re-mount (e.g. route change) but not a tab close.
  // Forwarded to chatAgent via the routeChat payload so the agent
  // can correlate turns into a single conversation server-side.
  const sessionIdRef = useRef<string>('');
  useEffect(() => {
    if (sessionIdRef.current) return;
    if (typeof window === 'undefined') return;
    const KEY = 'bc.chat.sessionId';
    let id = window.sessionStorage.getItem(KEY);
    if (!id) {
      // crypto.randomUUID is supported in every browser that supports
      // sessionStorage in 2026; the fallback covers truly ancient ones.
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        window.sessionStorage.setItem(KEY, id);
      } catch {
        // sessionStorage quota / disabled — just keep the in-memory id
      }
    }
    sessionIdRef.current = id;
  }, []);

  // First-open: seed the greeting + suggested chips from the stub
  // router. Calling routeChat with empty message and empty history
  // returns the canonical opener message.
  useEffect(() => {
    if (!open || hasOpened) return;
    setHasOpened(true);
    let cancelled = false;
    (async () => {
      const out = await routeChat({
        message: '',
        history: [],
        sessionId: sessionIdRef.current,
      });
      if (cancelled) return;
      setMessages([{ role: 'assistant', content: out.reply, ts: new Date().toISOString() }]);
      setActions(out.actions ?? []);
      window.setTimeout(() => inputRef.current?.focus(), 100);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hasOpened]);

  // Autoscroll on new message.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, busy]);

  // ESC closes when open (matches modal pattern across the site).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const send = async (text: string) => {
    const trimmed = text.trim().slice(0, MAX_INPUT_LENGTH); // hard cap input
    if (!trimmed || busy) return;

    // Per-session message cap — prevents a stuck or hostile loop from
    // hammering the agent backend. The user can still close and reopen
    // for a fresh limit if they genuinely have more to say after 30
    // turns, but at that point the right move is the contact form.
    if (messages.length >= MAX_MESSAGES_PER_SESSION) {
      setRateLimitMsg(
        "We've covered a lot: want me to follow up by email instead so we don't lose anything?",
      );
      setEscalating(true);
      return;
    }

    // Cooldown between sends. 800ms is below human typing-then-send
    // latency for almost anyone, so this is invisible to legitimate
    // users but blocks programmatic spam.
    const now = Date.now();
    const sinceLast = now - lastSendAtRef.current;
    if (sinceLast < MIN_SEND_INTERVAL_MS) {
      setRateLimitMsg('One second…');
      window.setTimeout(() => setRateLimitMsg(''), MIN_SEND_INTERVAL_MS - sinceLast);
      return;
    }
    lastSendAtRef.current = now;
    setRateLimitMsg('');

    const userMsg: ChatMessage = {
      role: 'user',
      content: trimmed,
      ts: new Date().toISOString(),
    };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setActions([]);
    setInput('');
    setBusy(true);
    try {
      // routeChat in chatRouter.ts already enforces a 30s timeout + output
      // sanitization + falls back to the rule-based stub on any failure.
      // So this try/catch is just the belt-and-suspenders layer for
      // truly unexpected throws (e.g. network blackout mid-await).
      const out = await routeChat({
        message: trimmed,
        history: nextHistory,
        sessionId: sessionIdRef.current,
      });
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: out.reply,
        ts: new Date().toISOString(),
      };
      setMessages((m) => [...m, assistantMsg]);
      setActions(out.actions ?? []);
      if (out.shouldEscalate) {
        // Surface the escalation form panel — visitor can drop their
        // email without leaving the conversation. Only auto-shows once
        // per session so the agent can suggest it again later without
        // forcing it open repeatedly.
        const seen = window.sessionStorage.getItem(ESCALATION_STORAGE_KEY);
        if (!seen) {
          window.sessionStorage.setItem(ESCALATION_STORAGE_KEY, '1');
          setEscalating(true);
        }
      }
    } catch (err) {
      console.error('[chat] routeChat threw:', err);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            "Hmm. I hit a snag on my end. Want me to follow up by email? Drop your address below.",
          ts: new Date().toISOString(),
        },
      ]);
      setEscalating(true);
    } finally {
      setBusy(false);
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  /**
   * A page chip moves the site underneath the chat, SPA-style, so the
   * conversation survives. Desktop keeps the panel open; on a phone the panel
   * covers the page, so it closes and the conversation is waiting when they
   * reopen it. Either way the next step — booking — stays one tap away.
   */
  const goToPage = (href: string, linkNavigates = false) => {
    if (!linkNavigates) navigate(href);
    const hasBooking = actions.some((c) => c.action?.startsWith('open-cal'));
    setMessages((m) => [
      ...m,
      {
        role: 'assistant',
        content: hasBooking
          ? "That page walks through it. When you're ready, book a call right here, no need to leave the chat."
          : "That page walks through it. Want to talk it through with us? You can book a call right here: the AuraOS walkthrough, or a free 15-min call if you just have questions.",
        ts: new Date().toISOString(),
      },
    ]);
    setActions((cur) => {
      const others = cur.filter((c) => c.href !== href);
      return others.some((c) => c.action?.startsWith('open-cal'))
        ? others
        : [{ label: 'Book your 15-minute walkthrough', action: 'open-cal-aios' as const }, { label: 'Free 15-min call for questions', action: 'open-cal-15min' as const }, ...others].slice(0, 5);
    });
    if (window.matchMedia('(max-width: 639px)').matches) setOpen(false);
  };

  const handleAction = (a: ChatAction) => {
    if (a.action === 'escalate') {
      setEscalating(true);
      return;
    }
    if (a.action === 'open-contact-form') {
      // Validate lockedService against the allowlist — routeChat
      // already sanitizes this, but defense-in-depth in case the chip
      // came from a different code path in the future.
      const locked =
        a.lockedService && VALID_LOCKED_SERVICES.has(a.lockedService)
          ? (a.lockedService as ContactFormService['lockedService'])
          : undefined;
      setContactModal({ lockedService: locked });
      setOpen(false);
      return;
    }
    if (a.action === 'open-cal-15min') {
      setBooking({ url: CONSULTATION_15MIN_URL, title: 'Free 15-min call' });
      return;
    }
    if (a.action === 'open-cal-consulting') {
      setBooking({ url: CONSULTING_BOOKING_URL, title: 'Operations Consulting' });
      return;
    }
    if (a.action === 'open-cal-aios') {
      setBooking({ url: walkthroughUrl('chat'), title: 'AuraOS walkthrough' });
      return;
    }
    // href fallthrough — only allow http(s) or absolute in-app paths.
    // (routeChat already strips javascript:/data: URLs before
    // they reach the widget; this second check guards manual chip
    // creators if they ever appear.)
    if (a.href) {
      if (/^https?:\/\//i.test(a.href)) {
        window.open(a.href, '_blank', 'noopener,noreferrer');
      } else if (a.href.startsWith('/')) {
        goToPage(a.href);
      } else {
        console.warn('[chat] dropping action with unsafe href:', a.href);
      }
    }
  };

  const submitEscalation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (escalateBusy || escalateSubmitted) return;
    setEscalateError('');

    // Honeypot — silently skip the API call on trip, show success.
    if (isHoneypotTripped(escalateHoneypot)) {
      setEscalateSubmitted(true);
      return;
    }

    // Visible-feedback email validation (was a silent return before).
    if (!isValidEmail(escalateEmail)) {
      setEscalateError('Please enter a valid email address.');
      return;
    }

    // Rate limit — 5-minute cooldown between escalation submissions
    // per device. Prevents the same visitor (or a script) from
    // pumping escalation leads through the chat path.
    const sinceLast = Date.now() - lastEscalationAt();
    if (sinceLast < ESCALATION_COOLDOWN_MS) {
      const minutesLeft = Math.ceil((ESCALATION_COOLDOWN_MS - sinceLast) / 60_000);
      setEscalateError(
        `You already sent one recently. Try again in about ${minutesLeft} minute${
          minutesLeft === 1 ? '' : 's'
        }, or email hello@brandctl.com directly.`,
      );
      return;
    }

    setEscalateBusy(true);

    // Cap transcript size so a long session can't bloat the
    // escalation lead. Last 20 messages, 500 chars each.
    const cappedTranscript = messages
      .slice(-20)
      .map(
        (m) =>
          `${m.role === 'user' ? 'Visitor' : 'Sam Assistant'}: ${m.content.slice(0, 500)}`,
      )
      .join('\n');
    const userNote = escalateMessage.trim().slice(0, 1000); // cap free-text
    const message = userNote
      ? `${userNote}\n\n---\nTranscript:\n${cappedTranscript}`
      : `Visitor requested human follow-up via chat widget.\n\n---\nTranscript:\n${cappedTranscript}`;
    try {
      await submitChatEscalation({
        email: escalateEmail.trim().slice(0, 254), // RFC 5321 max
        message,
      });
      markEscalationSubmitted();
      setEscalateSubmitted(true);
    } catch (err) {
      console.error('[chat] escalation submit failed:', err);
      setEscalateError(
        "Couldn't save that. Email hello@brandctl.com directly and we'll follow up.",
      );
    } finally {
      setEscalateBusy(false);
    }
  };

  return (
    <>
      {/* ─── Closed: floating avatar bubble ─────────────────────────── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="chat-bubble"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 20 }}
            // Press feedback on click/tap. The BC keycap itself depresses via
            // CSS (`.bc-fab:active .bc-key-face`), matching the brand keycap
            // that presses on :active; this whileTap adds a subtle whole-button
            // compress so the press reads as one tactile motion. Snappy
            // transition so it isn't caught by the delayed entrance timing.
            whileTap={{ y: 1, scale: 0.97, transition: { duration: 0.08, delay: 0 } }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1], delay: 1.2 }}
            className="bc-fab fixed bottom-6 right-5 md:right-6 z-[150] flex items-center gap-3 group"
            aria-label="Open the BRANDCENTRAL assistant"
          >
            <span className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur-md border border-[#e6e5e1] text-[#171718] text-xs font-sans font-bold uppercase tracking-[0.2em] shadow-[0_8px_32px_rgba(90,70,110,0.18)] opacity-0 group-hover:opacity-100 transition-opacity">
              Ask BRANDCENTRAL
            </span>
            {/* Outer relative wraps the circle so the green status dot
                can be positioned ABOVE the circle's edge instead of being
                clipped by it. The near-black fill makes the assistant pop
                against the light site — the dark BC keycap is used sparingly
                elsewhere, so it reads as its own distinct surface. The
                shadow is kept tight (small offset + negative spread) so it
                stays a soft, rounded halo instead of a drop that the screen
                edge clips flat at the bottom. */}
            <span className="relative shrink-0 group-hover:scale-105 transition-transform duration-300">
              <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#171718] ring-1 ring-white/10 shadow-[0_8px_22px_-6px_rgba(23,23,24,0.55)]">
                <BcMark size={38} tone="light" />
              </span>
              {/* Active status dot — lives OUTSIDE the circle so it overlays
                  the bottom-right edge cleanly. Border matches the button so
                  it reads as punched out of the dark surface. */}
              <span
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#171718] shadow-[0_0_8px_rgba(52,211,153,0.7)] animate-pulse"
                aria-hidden="true"
              />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ─── Open: chat panel ──────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            // `liquid-glass` is the site-wide glass class powering the
            // Services / Process / Contact / FAQs cards on the home
            // page (defined in src/index.css under @layer components).
            // Same recipe here so the chat panel reads as part of the
            // BRANDCENTRAL design system instead of a one-off widget.
            // The inline backdrop-filter override layers a stronger
            // blur + a slightly opaque dark tint on top so chat text
            // stays readable against the cinematic hero behind it —
            // the base .liquid-glass background is almost transparent
            // and was tuned for full-width cards on a black bg, not a
            // floating panel over arbitrary content.
            className="bc-chat fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[160] w-[calc(100vw-2.5rem)] sm:w-[380px] max-h-[calc(100vh-3rem)] md:max-h-[640px] flex flex-col rounded-3xl overflow-hidden border border-[#e6e5e1] shadow-[0_24px_80px_rgba(90,70,110,0.22)]"
            style={{ backgroundColor: '#ffffff' }}
            aria-modal="false"
            role="dialog"
            aria-label="BRANDCENTRAL assistant"
          >
            {/* Theme gradient accent bar */}
            <span
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-[3px] z-10"
              style={{ background: 'linear-gradient(90deg,#ff9e6b,#f5749f,#9a7bf0,#5f7bf0)' }}
            />
            {/* Header */}
            <header className="flex items-center gap-3 p-4 border-b border-[#eceae6]">
              {/* Header avatar — the BC keycap mark; the assistant is the
                  agency's agent, so it wears the brand, not a face. */}
              <span className="relative shrink-0">
                <span className="relative flex items-center justify-center w-11 h-11 rounded-full bg-white border border-[#e6e5e1] overflow-hidden">
                  <BcMark size={32} />
                </span>
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white shadow-[0_0_6px_rgba(52,211,153,0.65)]"
                  aria-hidden="true"
                />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[#171718] text-base leading-none font-bold">BRANDCENTRAL Assistant</p>
                {/* The green dot above the avatar carries the "online"
                    signal; the status line names who stands behind it. */}
                <p className="font-sans font-bold tracking-[0.18em] text-[#9a9b9e] text-[10px] uppercase mt-1.5">
                  Online · Trained on Samuel’s playbooks
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full bg-[#f4f3f1] hover:bg-[#eceae6] border border-[#e6e5e1] flex items-center justify-center text-[#57585b] hover:text-[#171718] transition-colors"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </header>

            {/* Inline booking — covers the conversation (which stays mounted
                underneath, scroll position and all) until they go back. */}
            {booking && (
              <div className="absolute inset-x-0 bottom-0 top-[3px] z-20 flex flex-col bg-white">
                <div className="flex items-center gap-2 p-3 border-b border-[#eceae6]">
                  <button
                    type="button"
                    onClick={() => setBooking(null)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#f4f3f1] hover:bg-[#eceae6] border border-[#e6e5e1] px-3 py-1.5 text-xs font-bold text-[#2a2a2c]"
                  >
                    <ArrowLeft size={13} /> Back to chat
                  </button>
                  <p className="flex-1 min-w-0 truncate text-sm font-bold text-[#171718]">{booking.title}</p>
                  <a
                    href={booking.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open booking in a new tab"
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#57585b] hover:text-[#171718] hover:bg-[#f4f3f1]"
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
                <iframe
                  title={`Book: ${booking.title}`}
                  src={`${booking.url}?embed=true&theme=light&layout=column_view`}
                  className="flex-1 w-full min-h-[460px] border-0"
                  data-lenis-prevent
                />
              </div>
            )}

            {/* Messages */}
            <div
              ref={scrollRef}
              data-lenis-prevent
              className="flex-1 overflow-y-auto p-4 space-y-3"
            >
              {messages.map((m, i) => (
                <div
                  key={`${m.ts}-${i}`}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl font-sans text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-[#111] text-white rounded-br-sm'
                        : 'bg-[#f4f3f1] border border-[#e8e7e4] text-[#2a2a2c] rounded-bl-sm'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {busy && (
                <div className="flex justify-start">
                  <div className="bg-[#f4f3f1] border border-[#e8e7e4] rounded-2xl rounded-bl-sm px-3.5 py-3">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#b7b6b2] animate-pulse" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#b7b6b2] animate-pulse [animation-delay:120ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#b7b6b2] animate-pulse [animation-delay:240ms]" />
                    </span>
                  </div>
                </div>
              )}

              {/* Quick-action chips — full nav-bar hover recipe via the
                  ChatChip component (white circle scales up from the
                  bottom of the pill, label slides up, black label
                  slides in from below). The .pill .pill-sm CSS pair
                  reuses every animation class the nav bar uses; only
                  the size envelope is reduced so 5 chips fit the
                  380px chat panel. */}
              {!busy && actions.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {actions.map((a) => {
                    // Internal absolute path → React Router Link so
                    // the SPA route loads in place. External http(s)
                    // URL → <a target=_blank>. action chips (escalate,
                    // open-contact-form, open-cal-*) → <button> with
                    // the existing handler.
                    if (a.href && !a.href.startsWith('http')) {
                      return (
                        <ChatChip
                          key={a.label}
                          label={a.label}
                          kind="link"
                          to={a.href}
                          // The Link itself navigates; this just keeps the
                          // conversation going on the new page.
                          onClick={() => goToPage(a.href!, true)}
                        />
                      );
                    }
                    if (a.href && /^https?:\/\//i.test(a.href)) {
                      return (
                        <ChatChip
                          key={a.label}
                          label={a.label}
                          kind="external"
                          externalHref={a.href}
                        />
                      );
                    }
                    return (
                      <ChatChip
                        key={a.label}
                        label={a.label}
                        kind="button"
                        onClick={() => handleAction(a)}
                      />
                    );
                  })}
                </div>
              )}

              {/* Escalation form — inline so the visitor never leaves the conversation */}
              {escalating && !escalateSubmitted && (
                <form
                  onSubmit={submitEscalation}
                  className="mt-2 p-3 rounded-2xl bg-[#faf9f7] border border-[#e8e7e4] space-y-2"
                >
                  <HoneypotField
                    value={escalateHoneypot}
                    onChange={setEscalateHoneypot}
                  />
                  {/* Hand-off moment — this is where Samuel shows up in
                      person, so his photo + name carry the personal
                      promise the branded agent above deliberately doesn't. */}
                  <div className="flex items-center gap-2.5">
                    <span className="relative shrink-0 block w-9 h-9 rounded-full bg-white overflow-hidden border border-[#e6e5e1]">
                      <img
                        src={avatarErrored ? SAM_AVATAR_FALLBACK : SAM_AVATAR_PATH}
                        alt="Samuel"
                        className="w-full h-full object-cover"
                        onError={() => setAvatarErrored(true)}
                        draggable={false}
                        loading="lazy"
                        decoding="async"
                        width="36"
                        height="36"
                      />
                    </span>
                    <p className="font-sans text-[#57585b] text-xs leading-relaxed">
                      Drop your email and <span className="text-[#171718] font-bold">Samuel</span> will
                      follow up personally. Anything he should know upfront?
                    </p>
                  </div>
                  <input
                    type="email"
                    required
                    maxLength={254}
                    value={escalateEmail}
                    onChange={(e) => {
                      setEscalateEmail(e.target.value);
                      if (escalateError) setEscalateError('');
                    }}
                    placeholder="name@yourcompany.com"
                    autoComplete="email"
                    className="w-full bg-white border border-[#e2e1de] rounded-lg p-2.5 text-[#171718] placeholder:text-[#9a9b9e] focus:outline-none focus:border-[#9a7bf0] transition-colors text-sm"
                  />
                  <textarea
                    value={escalateMessage}
                    onChange={(e) => setEscalateMessage(e.target.value.slice(0, 1000))}
                    placeholder="(Optional) What you're working on..."
                    rows={2}
                    maxLength={1000}
                    className="w-full bg-white border border-[#e2e1de] rounded-lg p-2.5 text-[#171718] placeholder:text-[#9a9b9e] focus:outline-none focus:border-[#9a7bf0] transition-colors text-sm resize-none"
                  />
                  {escalateError && (
                    <p
                      role="alert"
                      className="text-[11px] uppercase tracking-[0.18em] text-[#d6455f] font-bold"
                    >
                      {escalateError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={escalateBusy}
                    className="w-full py-2.5 bg-[#111] text-white font-sans font-bold text-[11px] uppercase tracking-[0.2em] rounded-full hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {escalateBusy ? 'Sending…' : 'Send to Samuel'}
                  </button>
                </form>
              )}
              {escalateSubmitted && (
                <div className="mt-2 p-3 rounded-2xl bg-[rgba(16,185,129,0.06)] border border-[rgba(16,185,129,0.3)]">
                  <p className="font-serif text-[#171718] text-base leading-tight font-bold">
                    Got it. Samuel will be in touch.
                  </p>
                  <p className="mt-1 font-sans text-[#57585b] text-xs">
                    Within a few hours, usually faster.
                  </p>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[#eceae6]">
              {/* Inline rate-limit / over-cap notice — shown only when
                  triggered so the row stays a single line at rest. */}
              {rateLimitMsg && (
                <p className="px-4 pt-2 text-[11px] uppercase tracking-[0.18em] text-[#9a9b9e] font-bold">
                  {rateLimitMsg}
                </p>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void send(input);
                }}
                className="p-3 flex items-center gap-2"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value.slice(0, MAX_INPUT_LENGTH))}
                  placeholder="Ask anything…"
                  disabled={busy}
                  maxLength={MAX_INPUT_LENGTH}
                  className="flex-1 min-w-0 bg-[#f7f6f4] border border-[#e2e1de] rounded-full px-4 py-2.5 text-[#171718] placeholder:text-[#9a9b9e] focus:outline-none focus:border-[#9a7bf0] focus:bg-white transition-colors text-sm disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  aria-label="Send message"
                  className="shrink-0 w-10 h-10 rounded-full bg-[#111] text-white flex items-center justify-center hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send size={15} />
                </button>
              </form>
              {/* Approaching-cap warning. Stays hidden until 1800/2000
                  chars so we don't introduce friction on normal use. */}
              {input.length >= INPUT_WARNING_AT && (
                <p className="px-4 pb-2 -mt-1 text-[10px] uppercase tracking-[0.18em] text-[#9a9b9e] font-bold text-right">
                  {input.length} / {MAX_INPUT_LENGTH}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* When a chip routes through to the main contact form, the
          widget closes and the modal opens. lockedService is forwarded
          so the form opens directly to that service path. */}
      {contactModal && (
        <ContactFormModal
          open={true}
          onClose={() => setContactModal(null)}
          lockedService={contactModal.lockedService}
        />
      )}
    </>
  );
};
